<script setup>
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DealMoreActions from './components/DealMoreActions.vue';

defineProps({
  headerTitle: { type: String, required: true },
  searchValue: { type: String, default: '' },
  viewMode: { type: String, default: 'kanban' },
  showViewToggle: { type: Boolean, default: true },
  hasActiveFilters: { type: Boolean, default: false },
});

/* eslint-disable vue/custom-event-name-casing */
const emit = defineEmits([
  'search',
  'create',
  'managePipeline',
  'toggleView',
  'filter',
  'import',
  'export',
  'select',
  'manage-fields',
]);
/* eslint-enable vue/custom-event-name-casing */
</script>

<template>
  <header class="sticky top-0 z-20 px-6 bg-n-surface-1">
    <div
      class="flex items-start sm:items-center justify-between w-full py-4 gap-2 mx-auto"
    >
      <div class="flex items-center gap-3 flex-shrink-0">
        <button
          v-if="showViewToggle"
          class="flex items-center justify-center size-8 rounded-lg border border-n-weak text-n-slate-11 hover:text-n-brand hover:border-n-brand transition-colors"
          :title="
            viewMode === 'kanban'
              ? $t('DEALS.VIEW.LIST')
              : $t('DEALS.VIEW.KANBAN')
          "
          @click="emit('toggleView')"
        >
          <Icon
            :icon="viewMode === 'kanban' ? 'i-lucide-list' : 'i-lucide-kanban'"
            class="size-4"
          />
        </button>
        <span class="text-xl font-medium truncate text-n-slate-12">
          {{ headerTitle }}
        </span>
      </div>
      <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-3">
        <div class="flex items-center gap-2 w-full sm:min-w-64">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('DEALS.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <button
            class="relative flex items-center justify-center size-8 rounded-lg border transition-colors"
            :class="
              hasActiveFilters
                ? 'border-n-brand text-n-brand bg-n-alpha-1'
                : 'border-n-weak text-n-slate-11 hover:text-n-brand hover:border-n-brand'
            "
            :title="$t('DEALS.FILTER.TITLE')"
            @click="emit('filter')"
          >
            <Icon icon="i-lucide-list-filter" class="size-4" />
            <span
              v-if="hasActiveFilters"
              class="absolute -top-1 -right-1 size-2 rounded-full bg-n-brand"
            />
          </button>
          <DealMoreActions
            @manage-pipeline="emit('managePipeline')"
            @import="emit('import')"
            @export="emit('export')"
            @select="emit('select')"
            @manage-fields="emit('manage-fields')"
          />
          <Button
            :label="$t('DEALS.ACTIONS.CREATE')"
            icon="i-lucide-plus"
            size="sm"
            @click="emit('create')"
          />
        </div>
      </div>
    </div>
  </header>
</template>
